/**
 * 팩 페이지 전용 예약 시간 포맷팅 유틸.
 */
import { formatTimeWithAM } from "../../../utils/date/format-date";
import {
  getDayOfWeekIndex,
  getTomorrowDayOfWeekIndex,
} from "../../../utils/reserve-time";

export type PackReserveTimeDay = {
  start?: string;
  end?: string;
};

export type PackReserveTimeMap = Record<string, PackReserveTimeDay | undefined>;

/** 오늘 픽업 시간 문자열 (formatTimeWithAM 적용) */
export function getReserveTime(
  reserveTime: PackReserveTimeMap | null | undefined
): string {
  const dayOfWeek = getDayOfWeekIndex(new Date());
  const day = reserveTime?.[dayOfWeek];
  if (!day?.start || !day?.end) return "-";
  return formatTimeWithAM(day.start) + " ~ " + formatTimeWithAM(day.end);
}

/** 내일 픽업 시간 문자열 (formatTimeWithAM 적용) */
export function getReserveTimeForTomorrow(
  reserveTime: PackReserveTimeMap | null | undefined
): string {
  const tomorrowOfWeek = getTomorrowDayOfWeekIndex();
  const day = reserveTime?.[tomorrowOfWeek];
  if (!day?.start || !day?.end) return "-";
  return formatTimeWithAM(day.start) + " ~ " + formatTimeWithAM(day.end);
}

/** 픽업 시작 30분 전 시간 (확정 시간) */
export function getReserveTimeBefore30Minutes(
  reserveTime: PackReserveTimeMap | null | undefined,
  isTomorrow: boolean
): string {
  const dayOfWeek = isTomorrow
    ? getTomorrowDayOfWeekIndex()
    : getDayOfWeekIndex(new Date());
  const start = reserveTime?.[dayOfWeek]?.start;
  if (!start) return "-";

  const [startHour, startMinute] = start.split(":").map(Number);
  const startDate = new Date();
  if (isTomorrow) {
    startDate.setDate(startDate.getDate() + 1);
  }
  startDate.setHours(startHour, startMinute - 30, 0, 0);
  const startBefore30Minutes = `${startDate
    .getHours()
    .toString()
    .padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`;
  return formatTimeWithAM(startBefore30Minutes);
}

/** dayOffset 기준 확정 시간 (HH:mm, 네비게이션용) */
export function calculateReserveTimeBefore30Minutes(
  reserveTime: PackReserveTimeMap | null | undefined,
  dayOffset: number
): string {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  const dayKey = getDayOfWeekIndex(date);
  const start = reserveTime?.[dayKey]?.start;
  if (!start) return "";
  const [startHour, startMinute] = start.split(":").map(Number);
  date.setHours(startHour, startMinute - 30);
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

/** 오늘 픽업 시간 raw (URL용, "HH:mm ~ HH:mm") */
export function getReserveTimeRaw(
  reserveTime: PackReserveTimeMap | null | undefined
): string {
  const dayOfWeek = getDayOfWeekIndex(new Date());
  const day = reserveTime?.[dayOfWeek];
  if (!day?.start || !day?.end) return "";
  return `${day.start} ~ ${day.end}`;
}

/** 내일 픽업 시간 raw (URL용, "HH:mm ~ HH:mm") */
export function getReserveTimeForTomorrowRaw(
  reserveTime: PackReserveTimeMap | null | undefined
): string {
  const tomorrowOfWeek = getTomorrowDayOfWeekIndex();
  const day = reserveTime?.[tomorrowOfWeek];
  if (!day?.start || !day?.end) return "";
  return `${day.start} ~ ${day.end}`;
}
